/**
 * E-Prescription Patient Connect Modal
 *
 * Modal for connecting a patient to DoseSpot for e-prescribing.
 * Collects/confirms patient information required for DoseSpot integration.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input';
import { useSnackbar } from 'notistack';

import { US_STATES } from '@shared/constants/states';

import { useConnectPatientMutation } from '../store';

/** Extract 10-digit phone number from formatted input */
const extractDigits = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  // Remove country code (1) if present
  return digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;
};

/** Map UI gender values to API values */
const mapGenderToApi = (gender: string): string => {
  // API accepts: male, female, unknown
  if (gender === 'other') return 'unknown';
  return gender;
};

/** DoseSpot API field length limits (v1.7.1, Dec 2025) */
const DOSESPOT_MAX = { name: 35, address: 35, city: 35 } as const;

interface PatientData {
  firstName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  phone?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface EPrescriptionPatientConnectModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  patientData?: PatientData;
  onSuccess?: () => void;
}

interface FormState {
  firstName: string;
  lastName: string;
  dob: string;
  gender: string;
  phone: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  weight: string;
  height: string;
}

const initialFormState: FormState = {
  firstName: '',
  lastName: '',
  dob: '',
  gender: '',
  phone: '',
  address: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  weight: '',
  height: '',
};

export function EPrescriptionPatientConnectModal({
  open,
  onClose,
  patientId,
  patientData,
  onSuccess,
}: EPrescriptionPatientConnectModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [connectPatient, { isLoading: isSubmitting }] = useConnectPatientMutation();
  const { jotrx } = useEventTracking();

  const [form, setForm] = useState<FormState>(initialFormState);
  const [error, setError] = useState<string | null>(null);
  const prefilledRef = useRef(false);

  const isUnder19 = useMemo(() => {
    if (!form.dob) return false;
    const today = new Date();
    const birth = new Date(form.dob);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age < 19;
  }, [form.dob]);

  // Pre-fill form with patient data on open, and once more if data arrives
  // late (slow network) so we don't strand the user with an empty form.
  // Once we've prefilled with real data, prefilledRef guards against
  // clobbering subsequent user edits.
  useEffect(() => {
    if (!open) {
      prefilledRef.current = false;
      return;
    }
    if (prefilledRef.current) return;

    if (patientData) {
      setForm({
        firstName: patientData.firstName || '',
        lastName: patientData.lastName || '',
        dob: patientData.dob || '',
        gender: patientData.gender?.toLowerCase() || '',
        phone: patientData.phone || '',
        address: patientData.address || '',
        address2: patientData.address2 || '',
        city: patientData.city || '',
        state: patientData.state || '',
        zipCode: patientData.zipCode || '',
        weight: '',
        height: '',
      });
      prefilledRef.current = true;
      setError(null);
    } else {
      // Modal opened with no demographics yet — show empty form and
      // wait for patientData to arrive (prefilledRef stays false).
      setForm(initialFormState);
      setError(null);
    }
  }, [open, patientData]);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const handlePhoneChange = (value: string) => {
    setForm(prev => ({ ...prev, phone: value }));
    if (error) setError(null);
  };

  const validateForm = (): boolean => {
    if (!form.firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!form.lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!form.dob.trim()) {
      setError('Date of birth is required');
      return false;
    }
    if (!form.gender) {
      setError('Gender is required');
      return false;
    }
    if (!matchIsValidTel(form.phone) || extractDigits(form.phone).length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    if (!form.address.trim()) {
      setError('Address is required');
      return false;
    }
    if (form.address.trim().length > DOSESPOT_MAX.address) {
      setError(`Street address must be ${DOSESPOT_MAX.address} characters or fewer. Use Address 2 for apartment, suite, or unit info.`);
      return false;
    }
    if (form.address2.trim().length > DOSESPOT_MAX.address) {
      setError(`Address 2 must be ${DOSESPOT_MAX.address} characters or fewer`);
      return false;
    }
    if (!form.city.trim()) {
      setError('City is required');
      return false;
    }
    if (!form.state.trim()) {
      setError('State is required');
      return false;
    }
    if (!(form.state.trim().toUpperCase() in US_STATES)) {
      setError('Please use a valid two-letter state abbreviation (e.g., MA, CA, NY)');
      return false;
    }
    if (!form.zipCode.trim()) {
      setError('ZIP code is required');
      return false;
    }
    if (isUnder19) {
      if (!form.weight.trim() || isNaN(Number(form.weight)) || Number(form.weight) <= 0) {
        setError('Weight is required for patients under 19');
        return false;
      }
      if (!form.height.trim() || isNaN(Number(form.height)) || Number(form.height) <= 0) {
        setError('Height is required for patients under 19');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      await connectPatient({
        patientId,
        connectPatientRequest: {
          patient: {
            first_name: form.firstName.trim(),
            last_name: form.lastName.trim(),
            dob: form.dob.trim(),
            gender: mapGenderToApi(form.gender),
            phone: extractDigits(form.phone),
            address: form.address.trim(),
            ...(form.address2.trim() && { address_2: form.address2.trim() }),
            city: form.city.trim(),
            state: form.state.trim(),
            zip_code: form.zipCode.trim(),
            ...(isUnder19 && form.weight.trim() && { weight: Number(form.weight) }),
            ...(isUnder19 && form.height.trim() && { height: Number(form.height) }),
          },
        },
      }).unwrap();

      enqueueSnackbar('Patient connected to DoseSpot successfully', { variant: 'success' });
      jotrx.patientConnected({ patient_id: patientId });
      onClose();
      onSuccess?.();
    } catch (err: unknown) {
      let errorMessage = 'Failed to connect patient. Please try again.';

      const parseValidationErrors = (errors: unknown[]): string => {
        return errors
          .map((e: unknown) => {
            const error = e as { msg?: string; loc?: string[] };
            return error.msg?.replace('Value error, ', '') || 'Validation error';
          })
          .join('. ');
      };

      // Handle error - RTK Query wraps as { data: { data: [...], message } }
      if (err && typeof err === 'object' && 'data' in err) {
        const outerData = (err as { data?: unknown }).data;

        if (outerData && typeof outerData === 'object') {
          const innerData = outerData as { data?: unknown; message?: string; detail?: unknown };

          // Check for nested data array (validation errors from auth0BaseQuery)
          if (Array.isArray(innerData.data)) {
            errorMessage = parseValidationErrors(innerData.data) || errorMessage;
          } else if (Array.isArray(innerData.detail)) {
            errorMessage = parseValidationErrors(innerData.detail) || errorMessage;
          } else if (innerData.message && innerData.message !== 'Unknown error occurred') {
            errorMessage = innerData.message;
          }
        } else if (Array.isArray(outerData)) {
          errorMessage = parseValidationErrors(outerData) || errorMessage;
        }
      }

      const errorType =
        err && typeof err === 'object' && 'status' in err
          ? String((err as { status: number }).status)
          : 'unknown';
      jotrx.patientConnectFailed({ patient_id: patientId, error_type: errorType });

      setError(errorMessage);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Connect Patient to E-Prescriptions</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
          Confirm or enter the patient&apos;s information to connect them to DoseSpot for
          e-prescribing. This only needs to be done once per patient.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={form.firstName}
              onChange={handleChange('firstName')}
              disabled={isSubmitting}
              required
              inputProps={{ maxLength: DOSESPOT_MAX.name }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={form.lastName}
              onChange={handleChange('lastName')}
              disabled={isSubmitting}
              required
              inputProps={{ maxLength: DOSESPOT_MAX.name }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              value={form.dob}
              onChange={handleChange('dob')}
              disabled={isSubmitting}
              required
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl component="fieldset" required>
              <FormLabel component="legend">Gender</FormLabel>
              <RadioGroup
                row
                value={form.gender}
                onChange={handleChange('gender')}
              >
                <FormControlLabel
                  value="male"
                  control={<Radio size="small" />}
                  label="Male"
                  disabled={isSubmitting}
                />
                <FormControlLabel
                  value="female"
                  control={<Radio size="small" />}
                  label="Female"
                  disabled={isSubmitting}
                />
                <FormControlLabel
                  value="other"
                  control={<Radio size="small" />}
                  label="Other"
                  disabled={isSubmitting}
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <MuiTelInput
              fullWidth
              label="Phone Number"
              value={form.phone}
              onChange={handlePhoneChange}
              disabled={isSubmitting}
              required
              defaultCountry="US"
              onlyCountries={['US']}
              forceCallingCode
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street Address"
              value={form.address}
              onChange={handleChange('address')}
              disabled={isSubmitting}
              required
              error={form.address.trim().length > DOSESPOT_MAX.address}
              helperText={
                form.address.trim().length > DOSESPOT_MAX.address
                  ? 'Max 35 characters — use Address 2 for the remainder'
                  : undefined
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address 2"
              placeholder="Apt, Suite, Unit, Building (optional)"
              value={form.address2}
              onChange={handleChange('address2')}
              disabled={isSubmitting}
              inputProps={{ maxLength: DOSESPOT_MAX.address }}
              error={form.address2.trim().length > DOSESPOT_MAX.address}
              helperText={
                form.address2.trim().length > DOSESPOT_MAX.address
                  ? `Must be ${DOSESPOT_MAX.address} characters or fewer`
                  : undefined
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={form.city}
              onChange={handleChange('city')}
              disabled={isSubmitting}
              required
              inputProps={{ maxLength: DOSESPOT_MAX.city }}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="State"
              value={form.state}
              onChange={handleChange('state')}
              disabled={isSubmitting}
              required
              placeholder="MA"
              inputProps={{ maxLength: 2 }}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="ZIP Code"
              value={form.zipCode}
              onChange={handleChange('zipCode')}
              disabled={isSubmitting}
              required
              placeholder="02101"
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          {isUnder19 && (
            <>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  DoseSpot requires height and weight for patients under 19.
                </Alert>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Weight (lbs)"
                  type="number"
                  value={form.weight}
                  onChange={handleChange('weight')}
                  disabled={isSubmitting}
                  required
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Height (inches)"
                  type="number"
                  value={form.height}
                  onChange={handleChange('height')}
                  disabled={isSubmitting}
                  required
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          {isSubmitting ? 'Connecting...' : 'Connect Patient'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
